/**
 * AppShell Component
 *
 * A responsive layout wrapper that provides the main application structure.
 * Handles sidebar, header, footer, and main content areas with responsive behavior.
 *
 * @fileoverview Main layout component for the multi-tenant UI
 *
 * @example
 * // Basic usage with all slots
 * <AppShell
 *   header={<Header title="Dashboard" />}
 *   sidebar={<Sidebar items={navItems} />}
 *   footer={<Footer copyright="2024" />}
 * >
 *   <YourContent />
 * </AppShell>
 *
 * @example
 * // Header-only layout (no sidebar)
 * <AppShell header={<Header title="Settings" />}>
 *   <SettingsPage />
 * </AppShell>
 *
 * @example
 * // Using the context hook in child components
 * function ChildComponent() {
 *   const { isSidebarOpen, toggleSidebar, isMobile } = useAppShell();
 *   return <Button onClick={toggleSidebar}>Toggle</Button>;
 * }
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  Box,
  Flex,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  useColorModeValue,
  type BoxProps,
} from '@chakra-ui/react';
import { useSidebarCollapse, isEditableTarget } from '../../hooks/useSidebarCollapse';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Configuration for sidebar width at different states
 */
export interface SidebarConfig {
  /** Width when sidebar is expanded (default: 256px) */
  expanded: string;
  /** Width when sidebar is collapsed (default: 64px) */
  collapsed: string;
}

/**
 * Context value provided to child components via useAppShell hook
 */
export interface AppShellContextValue {
  /** Whether the mobile sidebar drawer is open */
  isSidebarOpen: boolean;
  /** Open the sidebar drawer (mobile only) */
  openSidebar: () => void;
  /** Close the sidebar drawer (mobile only) */
  closeSidebar: () => void;
  /** Toggle the sidebar drawer (mobile only) */
  toggleSidebar: () => void;
  /** Whether the sidebar is in collapsed state (icons only) */
  isSidebarCollapsed: boolean;
  /** Toggle between expanded and collapsed sidebar */
  toggleSidebarCollapse: () => void;
  /** Current sidebar width based on collapsed state */
  sidebarWidth: string;
  /** Whether the current viewport is mobile */
  isMobile: boolean;
}

/**
 * Props for the AppShell component
 */
export interface AppShellProps extends Omit<BoxProps, 'children'> {
  /** Main content to render in the content area (required) */
  children: ReactNode;
  /** Optional sidebar component (renders on the left) */
  sidebar?: ReactNode;
  /** Optional header component (renders at the top) */
  header?: ReactNode;
  /** Optional footer component (renders at the bottom) */
  footer?: ReactNode;
  /** Initial collapsed state for sidebar (default: false), used only if nothing is stored yet */
  initialSidebarCollapsed?: boolean;
  /** Whether to show sidebar on mobile as drawer (default: true) */
  showSidebarOnMobile?: boolean;
  /** Custom padding for the main content area */
  contentPadding?: BoxProps['p'];
  /** Custom background for the main content area */
  contentBg?: BoxProps['bg'];
  /** Minimum height for the layout (default: 100vh) */
  minHeight?: BoxProps['minH'];
  /** Height of the `header` slot. Must match the Header's own `height` prop. */
  headerHeight?: string;
  /**
   * When true, the content area fills exactly the viewport height below the
   * header and never scrolls itself — the page becomes responsible for its
   * own internal scrolling (e.g. a table with its own scroll container).
   * Used by dense, data-table pages (`/transactions`, `/snapshots`) so a
   * sticky table header can anchor to a real scrollport instead of the
   * document. Every other route keeps the default: the page scrolls with
   * the document, like a normal Box.
   */
  fullHeight?: boolean;
  /** Disable the Ctrl/Cmd+B sidebar-collapse shortcut (default: false) */
  disableCollapseShortcut?: boolean;
}

// ============================================================================
// Default Configuration
// ============================================================================

/**
 * Default sidebar width configuration
 */
const DEFAULT_SIDEBAR_CONFIG: SidebarConfig = {
  expanded: '256px',
  collapsed: '64px',
};

/**
 * Default content padding responsive values
 */
const DEFAULT_CONTENT_PADDING = { base: 3, md: 4, lg: 4 };

const DEFAULT_HEADER_HEIGHT = '64px';

// ============================================================================
// Context Setup
// ============================================================================

/**
 * Context for sharing AppShell state with child components
 * Allows children to access sidebar state and control functions
 */
const AppShellContext = createContext<AppShellContextValue | null>(null);

/**
 * Hook to access AppShell context from child components
 *
 * @throws Error if used outside of AppShell component
 *
 * @example
 * function NavigationButton() {
 *   const { toggleSidebar, isMobile } = useAppShell();
 *   if (!isMobile) return null;
 *   return <IconButton onClick={toggleSidebar} icon={<MenuIcon />} />;
 * }
 */
export function useAppShell(): AppShellContextValue {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within an AppShell component');
  }
  return context;
}

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * AppShell - Main application layout component
 *
 * Provides a responsive shell with optional sidebar, header, and footer.
 * On mobile devices, the sidebar becomes a slide-out drawer.
 * On desktop, the sidebar is fixed on the left side.
 *
 * Features:
 * - Responsive sidebar (drawer on mobile, fixed on desktop)
 * - Collapsible sidebar support (expand/collapse to icons), persisted via
 *   `useSidebarCollapse` and toggleable with Ctrl/Cmd+B
 * - Context provider for child components to access layout state
 * - Customizable content area with padding and background
 * - Full TypeScript support with comprehensive types
 *
 * @param props - Component props (see AppShellProps interface)
 * @returns The rendered AppShell component
 */
export function AppShell({
  children,
  sidebar,
  header,
  footer,
  initialSidebarCollapsed = false,
  showSidebarOnMobile = true,
  contentPadding = DEFAULT_CONTENT_PADDING,
  contentBg,
  minHeight = '100vh',
  headerHeight = DEFAULT_HEADER_HEIGHT,
  fullHeight = false,
  disableCollapseShortcut = false,
  ...boxProps
}: AppShellProps): React.ReactElement {
  // -------------------------------------------------------------------------
  // State Management
  // -------------------------------------------------------------------------

  /**
   * Controls whether the mobile drawer is open. Not persisted — a drawer
   * should always start closed on a fresh page load.
   */
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  /**
   * Controls whether the desktop sidebar is collapsed (icons only).
   * Persisted in localStorage so it survives navigation and refresh.
   */
  const { isCollapsed: isSidebarCollapsed, toggle: toggleSidebarCollapse } =
    useSidebarCollapse(initialSidebarCollapsed);

  // -------------------------------------------------------------------------
  // Responsive Behavior
  // -------------------------------------------------------------------------

  /**
   * Determines if we're on a mobile viewport
   * Uses Chakra's useBreakpointValue for responsive detection
   */
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  /**
   * Calculate current sidebar width based on state
   */
  const sidebarWidth = isSidebarCollapsed
    ? DEFAULT_SIDEBAR_CONFIG.collapsed
    : DEFAULT_SIDEBAR_CONFIG.expanded;

  // -------------------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------------------

  /**
   * Open the mobile sidebar drawer
   */
  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  /**
   * Close the mobile sidebar drawer
   */
  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  /**
   * Toggle the mobile sidebar drawer
   */
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // -------------------------------------------------------------------------
  // Keyboard shortcut: Ctrl/Cmd+B toggles the desktop sidebar collapse
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (disableCollapseShortcut) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b') return;
      if (!(event.ctrlKey || event.metaKey)) return;
      // Don't steal Ctrl/Cmd+B from a text field (e.g. the transactions
      // search box) where it might be a native/browser text action.
      if (isEditableTarget(event.target)) return;

      event.preventDefault();
      toggleSidebarCollapse();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disableCollapseShortcut, toggleSidebarCollapse]);

  // -------------------------------------------------------------------------
  // Memoized Context Value
  // -------------------------------------------------------------------------

  /**
   * Memoize context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<AppShellContextValue>(
    () => ({
      isSidebarOpen,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      isSidebarCollapsed,
      toggleSidebarCollapse,
      sidebarWidth,
      isMobile,
    }),
    [
      isSidebarOpen,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      isSidebarCollapsed,
      toggleSidebarCollapse,
      sidebarWidth,
      isMobile,
    ]
  );

  // -------------------------------------------------------------------------
  // Theming - Uses useColorModeValue for light/dark mode support
  // -------------------------------------------------------------------------

  /**
   * Background color for content area
   */
  const defaultContentBg = useColorModeValue('gray.50', 'xacto.deepNavy');
  const resolvedContentBg = contentBg ?? defaultContentBg;

  /**
   * Background for sidebar
   */
  const sidebarBg = useColorModeValue('white', 'xacto.darkSurface');

  /**
   * Border color for sidebar
   */
  const sidebarBorderColor = useColorModeValue('gray.200', 'xacto.mutedNavy');

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /**
   * Renders the sidebar content wrapped in appropriate container
   * On mobile: Renders inside a Drawer component
   * On desktop: Renders as a fixed sidebar
   */
  const renderSidebar = () => {
    if (!sidebar) return null;

    // Mobile: Render sidebar in a drawer
    if (isMobile && showSidebarOnMobile) {
      return (
        <Drawer isOpen={isSidebarOpen} placement="left" onClose={closeSidebar} size="xs">
          <DrawerOverlay />
          <DrawerContent bg={sidebarBg}>
            <DrawerCloseButton />
            <Box pt={10}>{sidebar}</Box>
          </DrawerContent>
        </Drawer>
      );
    }

    // Desktop: Render fixed sidebar
    if (!isMobile) {
      return (
        <Box
          as="aside"
          position="fixed"
          left={0}
          top={header ? 'var(--xacto-header-h)' : 0}
          bottom={0}
          w="var(--xacto-sidebar-w)"
          bg={sidebarBg}
          borderRight="1px"
          borderColor={sidebarBorderColor}
          overflowY="auto"
          transition="width 0.2s ease-in-out"
          zIndex={10}
        >
          {sidebar}
        </Box>
      );
    }

    return null;
  };

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <AppShellContext.Provider value={contextValue}>
      <Box
        // No minH in fullHeight mode: the sx block below sets an explicit
        // height, and a `min-height: 100vh` would win over `height: 100dvh`
        // on mobile (where dvh < vh once browser chrome is showing),
        // defeating the dvh fallback and pushing content under the URL bar.
        minH={fullHeight ? undefined : minHeight}
        sx={{
          // Single source of truth for the two dimensions every fixed/sticky
          // child in this shell (header, sidebar, content offset) needs.
          // Kept as CSS custom properties instead of duplicated literals so
          // resizing the sidebar (collapsed/expanded) or the header never
          // has to be changed in more than one place.
          '--xacto-sidebar-w': sidebar && !isMobile ? sidebarWidth : '0px',
          '--xacto-header-h': header ? headerHeight : '0px',
          // 100dvh accounts for mobile browser chrome (address bar) that
          // 100vh doesn't; fall back to 100vh where dvh isn't supported.
          ...(fullHeight && {
            height: '100vh',
            '@supports (height: 100dvh)': { height: '100dvh' },
          }),
        }}
        {...boxProps}
      >
        {/* Header - Fixed at top */}
        {header && (
          <Box
            as="header"
            position="fixed"
            top={0}
            left={0}
            right={0}
            h="var(--xacto-header-h)"
            zIndex={20}
          >
            {header}
          </Box>
        )}

        {/* Sidebar - Fixed on left (desktop) or Drawer (mobile) */}
        {renderSidebar()}

        {/* Main Content Area */}
        <Flex
          direction="column"
          minH={fullHeight ? undefined : minHeight}
          ml={sidebar && !isMobile ? 'var(--xacto-sidebar-w)' : 0}
          pt="var(--xacto-header-h)"
          transition="margin-left 0.2s ease-in-out"
          sx={
            fullHeight
              ? {
                  height: '100vh',
                  '@supports (height: 100dvh)': { height: '100dvh' },
                }
              : undefined
          }
        >
          {/* Main Content */}
          <Box
            as="main"
            flex={1}
            minH={0}
            overflow={fullHeight ? 'hidden' : undefined}
            display={fullHeight ? 'flex' : undefined}
            flexDirection={fullHeight ? 'column' : undefined}
            p={fullHeight ? 0 : contentPadding}
            bg={resolvedContentBg}
          >
            {children}
          </Box>

          {/* Footer - At bottom */}
          {footer && (
            <Box as="footer">
              {footer}
            </Box>
          )}
        </Flex>
      </Box>
    </AppShellContext.Provider>
  );
}

// ============================================================================
// Exports
// ============================================================================

export default AppShell;
